import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // SEO Integration
import api from "../../api/axios";

const CategoryPage = () => {
  const { categorie } = useParams(); 
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  // --- SEO HELPER ---
  // Formats 'lave-linge' into 'Lave Linge' for titles
  const formattedCategory = categorie ? categorie.replace(/-/g, ' ') : 'Produits';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/categorie/cat?cat=${categorie}`);
        if (res.data.success) {
          setProducts(res.data.data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categorie]);

  const ProductCard = ({ product }) => {
    const price = parseFloat(product.price);
    const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
    const discount = (oldPrice && oldPrice > price) 
      ? Math.round(((oldPrice - price) / oldPrice) * 100) 
      : 0;

    const imageSrc = product.main_image.startsWith('/storage') 
      ? `${BACKEND_BASE}${product.main_image}` 
      : `${BACKEND_BASE}/storage/${product.main_image}`;

    return (
      <Link 
        to={`/product/${product.slug}`} 
        className="group flex flex-col bg-white p-4 transition-all duration-300 hover:shadow-2xl border border-gray-50 rounded-2xl"
      >
        <div className="relative mb-4 h-40 w-full overflow-hidden sm:h-56">
          {discount > 0 && (
            <div className="absolute top-0 left-0 z-10 bg-[#ffaa00] px-3 py-1 text-[10px] font-black text-white uppercase tracking-wider rounded-br-lg">
              -{discount}%
            </div>
          )}
          <img 
            src={imageSrc} 
            alt={`${product.brand} ${product.title}`} // Enhanced Alt Text
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
            loading="lazy" // Performance boost
          />
        </div>
        <div className="flex flex-col text-center mt-auto">
          <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-widest mb-1">
            {product.brand || 'Electrotitan'}
          </span>
          <h3 className="font-sans text-xs font-bold text-gray-800 line-clamp-2 min-h-[32px] mb-2 uppercase tracking-tight group-hover:text-[#ffaa00] transition-colors">
            {product.title}
          </h3>
          <div className="flex flex-col items-center gap-1">
            <span className="text-lg font-black text-[#ffaa00]">
              {product.price} <span className="text-[10px]">DH</span>
            </span>
            {product.old_price && (
              <span className="text-xs text-[#8A8A89] line-through font-bold">
                {product.old_price} DH
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      {/* --- SEO SECTION START --- */}
      <Helmet>
        <title>{`${formattedCategory.toUpperCase()} | Électroménager Electro Titan Maroc`}</title>
        <meta 
          key="description" name="description" 
          content={`Découvrez notre sélection de ${formattedCategory} chez Electro Titan. Les meilleures marques au meilleur prix à Salé et partout au Maroc.`} 
        />
        <link rel="canonical" href={window.location.href} />
        <meta key="og:title" property="og:title" content={`${formattedCategory.toUpperCase()} - Electro Titan`} />
        <meta key="og:description" property="og:description" content={`Achetez votre ${formattedCategory} en ligne. Livraison rapide et SAV garanti au Maroc.`} />
        
        {/* CollectionPage Schema for better indexing of product lists */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": formattedCategory,
            "url": window.location.href,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": products.length,
              "itemListElement": products.map((p, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "url": `https://www.titanelectro.ma/product/${p.slug}`
              }))
            }
          })}
        </script>
      </Helmet>
      {/* --- SEO SECTION END --- */}

      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-black uppercase tracking-tighter">
            {formattedCategory}
          </h1>
          <p className="text-sm font-bold text-[#8A8A89]">
            {loading ? 'Chargement...' : `Affichage de ${products.length} produits`}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {products.length === 0 && (
                  <div className="text-center py-20 text-gray-400 font-bold uppercase tracking-widest">
                    Aucun produit trouvé dans cette catégorie.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE FILTER DRAWER */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div 
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              className="fixed inset-y-0 left-0 w-[80%] max-w-xs bg-white z-50 p-8 lg:hidden shadow-2xl"
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-black uppercase tracking-widest">Filtres</h2>
                <button onClick={() => setShowMobileFilters(false)}><X size={24} /></button>
              </div>
              <button 
                onClick={() => setShowMobileFilters(false)}
                className="w-full mt-10 bg-[#ffaa00] text-white py-4 rounded-xl font-bold uppercase tracking-widest shadow-lg"
              >
                Appliquer
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryPage;